import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errMsg, useApi } from "@/lib/api";
import { rm, tarikh } from "@/pages/AkaunPage";

const inp = "h-10 px-3 rounded-lg bg-bg border border-line text-sm focus:outline-none focus:border-[var(--q)]";

const PromoForm = ({ onDone }) => {
  const api = useApi();
  const [f, setF] = useState({ kod: "", harga: "39", had: "", tamat: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: k === "kod" ? e.target.value.toUpperCase() : e.target.value });
  const harga = Math.round(Number(f.harga) * 100);
  const ok = /^[A-Z0-9_-]{3,30}$/.test(f.kod) && harga >= 100 && harga <= 4800;
  const save = async () => {
    setBusy(true);
    try {
      await api("post", "/admin/promo", { kod: f.kod, produk: "tanpahad", harga_sen: harga, had_guna: f.had ? Number(f.had) : null, tamat: f.tamat || null });
      toast.success(`Kod ${f.kod} dicipta`); setF({ kod: "", harga: "39", had: "", tamat: "" }); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="surface rounded-xl p-4 grid sm:grid-cols-5 gap-3 items-end" data-testid="promo-form">
      <label className="text-xs text-mute space-y-1">Kod<input value={f.kod} onChange={set("kod")} placeholder="BARU10" className={`${inp} w-full font-mono-data`} data-testid="promo-kod" /></label>
      <label className="text-xs text-mute space-y-1">Harga pelan RM49 (RM)
        <select value={f.harga} onChange={set("harga")} className={`${inp} w-full`} data-testid="promo-harga">
          <option value="39">RM39</option><option value="29">RM29</option>
        </select>
      </label>
      <label className="text-xs text-mute space-y-1">Had guna (pilihan)<input type="number" min="1" value={f.had} onChange={set("had")} className={`${inp} w-full`} data-testid="promo-had" /></label>
      <label className="text-xs text-mute space-y-1">Tarikh tamat (pilihan)<input type="date" value={f.tamat} onChange={set("tamat")} className={`${inp} w-full`} data-testid="promo-tamat" /></label>
      <button onClick={save} disabled={busy || !ok} className="h-10 px-5 rounded-full bg-ink text-bg text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40" data-testid="promo-save">
        {busy && <Loader2 size={14} className="animate-spin" />} Cipta kod
      </button>
    </div>
  );
};

export const PromoAdmin = () => {
  const api = useApi();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api("get", "/admin/promo").then(setItems).catch(() => setItems([])), [api]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (p) => { try { await api("patch", `/admin/promo/${p.id}`, { aktif: !p.aktif }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (p) => { if (!window.confirm(`Padam kod ${p.kod}? Jika masih ada bayaran menunggu dengan kod ini, lebih baik nyahaktifkan sahaja.`)) return; try { await api("delete", `/admin/promo/${p.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4" data-testid="admin-promo">
      <p className="text-sm text-mute max-w-3xl">Kod promosi untuk pelan RM49 (tanpa had). <strong className="text-ink">Penting:</strong> cipta kupon dengan kod dan harga yang <em>sama</em> dalam dashboard OnPay (Senarai &gt; Kupon), dan aktifkan fungsi kupon pada borang RM49. OnPay tidak membenarkan kod diisi secara automatik, jadi pengguna akan diminta menaip kod di borang OnPay.</p>
      <PromoForm onDone={load} />
      {!items ? <Loader2 className="animate-spin text-q" /> : (
        <div className="rounded-xl border border-line overflow-x-auto surface">
          <Table>
            <TableHeader><TableRow><TableHead>Kod</TableHead><TableHead className="text-right">Harga</TableHead><TableHead className="text-right">Guna</TableHead><TableHead>Tamat</TableHead><TableHead>Aktif</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`promo-row-${p.kod}`} className={p.aktif ? "" : "opacity-50"}>
                  <TableCell className="font-mono-data">{p.kod}</TableCell>
                  <TableCell className="text-right font-mono-data">{rm(p.harga_sen)}</TableCell>
                  <TableCell className="text-right font-mono-data">{p.guna || 0}{p.had_guna ? ` / ${p.had_guna}` : ""}</TableCell>
                  <TableCell className="text-xs">{p.tamat ? tarikh(p.tamat) : "—"}</TableCell>
                  <TableCell><Switch checked={p.aktif} onCheckedChange={() => toggle(p)} data-testid={`promo-toggle-${p.kod}`} /></TableCell>
                  <TableCell><button onClick={() => del(p)} aria-label="Padam" className="text-mute hover:text-warn" data-testid={`promo-delete-${p.kod}`}><Trash2 size={15} /></button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {items.length === 0 && <p className="p-4 text-sm text-mute" data-testid="promo-empty">Belum ada kod promosi.</p>}
        </div>
      )}
    </div>
  );
};
